import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import SchemaForm from '@department-of-veterans-affairs/platform-forms-system/SchemaForm';
import { VaButton } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';
import { useEditOrAddForm } from 'platform/forms-system/src/js/patterns/array-builder';
import { useDuplicateChecks } from 'platform/forms-system/src/js/patterns/array-builder/useDuplicateChecks';
import { useItemPageGuard } from 'platform/forms-system/src/js/patterns/array-builder/useItemPageGuard';
import ArrayBuilderCancelButton from 'platform/forms-system/src/js/patterns/array-builder/ArrayBuilderCancelButton';
import * as helpers from 'platform/forms-system/src/js/patterns/array-builder/helpers';
import { syncIncomeSourcePayer } from '../helpers';

/**
 * Array-builder item page for income sources.
 *
 * Mirrors ArrayBuilderItemPage, but syncs payer to "SSA" when the income type
 * is Social Security (and clears that default when switching away). Needed
 * because updateFormData only runs in add mode — edit mode keeps local state
 * in useEditOrAddForm and never calls FormPage.onChange until submit.
 *
 * @type {CustomPageType}
 */
const IncomeSourceItemPage = props => {
  const arrayBuilderProps = props.arrayBuilder;

  const {
    arrayPath,
    getSummaryPath,
    getIntroPath,
    reviewRoute,
    getText,
    required,
  } = arrayBuilderProps;

  const searchParams = helpers.getArrayUrlSearchParams();
  const isEdit = !!searchParams.get('edit');
  const isAdd = !!searchParams.get('add');
  const introRoute = getIntroPath(props.fullData);
  const summaryRoute = getSummaryPath(props.fullData);

  const { data, schema, uiSchema, onChange, onSubmit } = useEditOrAddForm({
    isEdit,
    schema: props.schema,
    uiSchema: props.uiSchema,
    data: props.data,
    fullData: props.fullData || {},
    onChange: props.onChange,
    onSubmit: props.onSubmit,
    index: props.pagePerItemIndex ? parseInt(props.pagePerItemIndex, 10) : null,
    arrayPath,
  });

  const handleChange = useCallback(
    updatedData => {
      onChange(syncIncomeSourcePayer(updatedData, undefined, data));
    },
    [data, onChange],
  );

  const { checkForDuplicates, renderDuplicateModal } = useDuplicateChecks({
    arrayBuilderProps,
    customPageProps: props,
  });
  const handleSubmit = checkForDuplicates(onSubmit);

  const shouldRender = useItemPageGuard({
    arrayBuilderProps,
    customPageProps: props,
    schema,
  });

  if (!shouldRender) {
    return null;
  }

  const NavButtons = props.NavButtons || FormNavButtons;

  return (
    <>
      {renderDuplicateModal()}
      <SchemaForm
        name={props.name}
        title={props.title}
        data={data}
        appStateData={props.appStateData}
        schema={schema}
        uiSchema={uiSchema}
        pagePerItemIndex={props.pagePerItemIndex}
        formContext={props.formContext}
        formOptions={props.formOptions}
        getFormData={props.getFormData}
        trackingPrefix={props.trackingPrefix}
        uploadFile={props.uploadFile}
        onChange={handleChange}
        onSubmit={handleSubmit}
      >
        <>
          {isAdd && (
            <>
              <ArrayBuilderCancelButton
                goToPath={props.goToPath}
                arrayPath={arrayPath}
                summaryRoute={summaryRoute}
                introRoute={introRoute}
                reviewRoute={reviewRoute}
                getText={getText}
                required={required}
              />
              {props.pageContentBeforeButtons}
              {props.contentBeforeButtons}
              <NavButtons
                goBack={props.goBack}
                goForward={props.onContinue}
                submitToContinue
                enableAnalytics
                useWebComponents={
                  props.formOptions?.useWebComponentForNavigation
                }
              />
            </>
          )}
          {isEdit && (
            <div className="vads-u-display--flex">
              <div className="vads-u-margin-right--2">
                <ArrayBuilderCancelButton
                  goToPath={props.goToPath}
                  arrayPath={arrayPath}
                  summaryRoute={summaryRoute}
                  introRoute={introRoute}
                  reviewRoute={reviewRoute}
                  getText={getText}
                  required={required}
                  className="vads-u-margin-0"
                />
              </div>
              <div>
                <VaButton
                  continue
                  submit="prevent"
                  text={getText('editSaveButtonText')}
                />
              </div>
            </div>
          )}

          {props.contentAfterButtons}
        </>
      </SchemaForm>
    </>
  );
};

IncomeSourceItemPage.propTypes = {
  arrayBuilder: PropTypes.shape({
    arrayPath: PropTypes.string.isRequired,
    getIntroPath: PropTypes.func.isRequired,
    getSummaryPath: PropTypes.func.isRequired,
    getText: PropTypes.func.isRequired,
    required: PropTypes.func.isRequired,
    reviewRoute: PropTypes.string.isRequired,
  }).isRequired,
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  NavButtons: PropTypes.func,
  appStateData: PropTypes.object,
  contentAfterButtons: PropTypes.node,
  contentBeforeButtons: PropTypes.node,
  data: PropTypes.object,
  formContext: PropTypes.object,
  formOptions: PropTypes.object,
  fullData: PropTypes.object,
  getFormData: PropTypes.func,
  goBack: PropTypes.func,
  goToPath: PropTypes.func,
  pageContentBeforeButtons: PropTypes.node,
  pagePerItemIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  trackingPrefix: PropTypes.string,
  uploadFile: PropTypes.func,
  onChange: PropTypes.func,
  onContinue: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default IncomeSourceItemPage;
